import { css } from 'twin.macro';
import React, { useEffect, useState, useRef } from 'react';
import Video, { createLocalVideoTrack } from 'twilio-video';
import { connect, useSelector } from 'react-redux';
import { RoomContainer } from './styles/RoomStyles';
import { MdCallEnd, MdScreenShare, MdStopScreenShare } from 'react-icons/md';
import { AiOutlineAudio, AiOutlineAudioMuted } from 'react-icons/ai';
import { FiCameraOff, FiCamera, FiSettings, FiShare2 } from 'react-icons/fi';
import SideMenu from './SideMenu/SideMenu';
import { BsChatDotsFill, BsPeopleFill, BsRecordCircle, BsRecordFill, BsThreeDotsVertical } from 'react-icons/bs';
import logo from '../../../assets/logos/logo-xs.png';
import { getChannelMembers, getTeamMember } from '../../../_redux/actions/ActionOrganization';
import GridLayout from './Layout/GridLayout';
import { ButtonGroup, Dropdown, DropdownButton } from 'react-bootstrap';
import MeetingTimer from './components/MeetingTimer';
import { toast } from 'react-toastify';
import { conferenceApi } from '@/_services/api/conference';
import { FaStop } from 'react-icons/fa';
import Swal from 'sweetalert2';

const Room = ({
    user,
    room,
    conferenceId,
    handleDisconnect,
    handleEndCall,
    audio,
    video,
    handleAudio,
    handleVideo,
    handleRecording = () => {},
    allParticipantsInfo,
    waitingParticipants,
    participantInfo,
    channelInfo,
    getTeamMember,
    getChannelMembers,
    conferenceHostId,
    handleMuteParticipant,
    handleRemoveParticipant,
    conferenceSettings,
    updateConferenceSettings,
    recording,
}) => {
    const organization = useSelector((state) => {
        const organizations = state?.organizations?.organizations;
        return Array.isArray(organizations) && organizations.length > 0 ? organizations[0] : null;
    });
    const [sharingScreen, setSharingScreen] = useState(false);
    const [participants, setParticipants] = useState([]);
    const [dominantSpeaker, setDominantSpeaker] = useState(null);
    const [isMobileView, setIsMobileView] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 900);
    const [sideMenuActiveTab, setSideMenuActiveTab] = useState(() =>
        typeof window !== 'undefined' && window.innerWidth <= 900 ? false : 'People'
    );
    const localScreenRef = useRef();
    const localVideoRef = useRef();
    const isHost = (user?._id || user?.id) === conferenceHostId;

    const handleCloseSideMenu = () => {
        setSideMenuActiveTab(false);
    };

    const handleSideMenuActiveTab = (tab) => {
        if (sideMenuActiveTab === tab) {
            setSideMenuActiveTab(false);
        } else {
            setSideMenuActiveTab(tab);
        }
    };

    const handleShareConferenceLink = async () => {
        try {
            const conferenceUrl = window.location.href;
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(conferenceUrl);
                toast.info('Copied conference link');
            }
        } catch (error) {
            toast.error('Failed to copy conference link');
        }
    };

    useEffect(() => {
        const onResize = () => {
            const mobile = window.innerWidth <= 900;
            setIsMobileView(mobile);
        };
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, []);
    useEffect(() => {
        if (channelInfo) {
            getTeamMember(channelInfo?.team?._id, organization.id);
            getChannelMembers(channelInfo?._id || channelInfo.id);
        }
    }, [channelInfo, channelInfo.id, channelInfo?.type, channelInfo?.team?._id, organization.id]);

    useEffect(() => {
        const participantConnected = (participant) => {
            setParticipants((prevParticipants) => {
                const existingIndex = prevParticipants.findIndex((p) => p?.identity === participant?.identity);
                if (existingIndex !== -1) {
                    const newParticipants = [...prevParticipants];
                    newParticipants[existingIndex] = participant;
                    return newParticipants;
                }
                return [...prevParticipants, participant];
            });
        };

        const participantDisconnected = (participant) => {
            setParticipants((prevParticipants) => prevParticipants.filter((p) => p.identity !== participant.identity));
        };

        room.on('participantConnected', participantConnected);
        room.on('participantDisconnected', participantDisconnected);
        room.participants.forEach(participantConnected);
        room.on('dominantSpeakerChanged', (participant) => {
            setDominantSpeaker(participant);
        });
        return () => {
            room.off('participantConnected', participantConnected);
            room.off('participantDisconnected', participantDisconnected);
        };
    }, [room]);

    const handleMute = () => {
        if (!room) return;

        const audioTracks = Array.from(room.localParticipant.audioTracks.values());

        if (audioTracks.length === 0) {
            toast.warning('No audio tracks found. Please reload conference');
            return console.error('No audio tracks found');
        }

        const targetState = !audio;
        audioTracks.forEach((publication) => {
            if (publication.track) {
                targetState ? publication.track.enable() : publication.track.disable();
            }
        });

        handleAudio({ target: { checked: targetState } });
    };

    const handleCamera = async () => {
        if (room) {
            if (!video) {
                handleVideo({ target: { checked: true } });
                const videoTrack = await createLocalVideoTrack({
                    height: 720,
                    frameRate: 24,
                    width: 1280,
                    name: 'camera',
                });
                room.localParticipant.publishTrack(videoTrack);
                videoTrack.attach(localVideoRef.current);
                await conferenceApi.updateConference(conferenceId, { hasCameraShare: true });
            } else {
                handleVideo({ target: { checked: false } });
                room.localParticipant.videoTracks.forEach((track) => {
                    if (track.trackName === 'camera') {
                        track.track.stop();
                        track.unpublish();
                    }
                });
            }
        }
    };

    const handleScreenShare = () => {
        if (room) {
            if (!sharingScreen) {
                navigator.mediaDevices
                    .getDisplayMedia({ video: { frameRate: 10 }, audio: false })
                    .then(async (stream) => {
                        const screenTrack = new Video.LocalVideoTrack(stream.getTracks()[0], { name: 'screenShare' });
                        room.localParticipant.publishTrack(screenTrack);
                        setSharingScreen(true);
                        screenTrack.attach(localScreenRef.current);
                        screenTrack.once('stopped', () => {
                            room.localParticipant.unpublishTrack(screenTrack);
                            setSharingScreen(false);
                        });
                        await conferenceApi.updateConference(conferenceId, { hasScreenShare: true });
                    })
                    .catch((e) => {
                        console.log('Could not share the screen.', e);
                    });
            } else {
                room.localParticipant.videoTracks.forEach((track) => {
                    if (track.trackName === 'screenShare') {
                        track.track.stop();
                    }
                });
                setSharingScreen(false);
            }
        }
    };

    const handleConferenceRecording = async () => {
        try {
            if (recording) {
                const confirm = await Swal.fire({
                    title: 'Are you sure want to stop/pause recording?',
                    icon: 'warning',
                    confirmButtonColor: '#d33',
                    confirmButtonText: 'Stop/Pause Recording',
                });
                if (confirm.isConfirmed) {
                    await conferenceApi.stopRecording(conferenceId);
                    handleRecording(false);
                }
            } else {
                const confirm = await Swal.fire({
                    title: 'Are you sure want to start recording?',
                    icon: 'warning',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: 'Yes, Record',
                });
                if (confirm.isConfirmed) {
                    await conferenceApi.startRecording(conferenceId);
                    handleRecording(true);
                }
            }
        } catch (error) {
            toast.error(error.message || `Failed to ${recording ? 'stop' : 'start'} recording`);
        }
    };
    return (
        <RoomContainer>
            <div className="room">
                <div
                    css={css`
                        width: ${sideMenuActiveTab ? 'calc(100% - max(358px, 25%))' : '100%'};
                        transition: width 200ms ease;
                        @media (max-width: 900px) {
                            width: 100%;
                        }
                    `}
                >
                    <div
                        css={css`
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            padding-top: 1rem;
                            gap: 0.75rem;
                            @media (max-width: 900px) {
                                justify-content: flex-start;
                                align-items: flex-start;
                            }
                        `}
                    >
                        <div
                            css={css`
                                width: 60px;
                                margin-left: 8px;
                                @media (max-width: 1000px) {
                                    width: 40px;
                                }
                            `}
                        >
                            <img
                                src={logo}
                                css={css`
                                    width: 100%;
                                `}
                                alt="logo"
                            />
                        </div>
                        <div
                            className="d-flex flex-column"
                            css={css`
                                min-width: 0;
                                flex: 1;
                            `}
                        >
                            <div>
                                <h2
                                    css={css`
                                        margin: 0;
                                        word-break: break-word;
                                        white-space: normal;
                                        @media (max-width: 900px) {
                                            font-size: 1.25rem;
                                            line-height: 1.2;
                                            text-align: left;
                                        }
                                    `}
                                >
                                    {recording && (
                                        <BsRecordFill
                                            color="#D73535"
                                            style={{
                                                fontSize: '18px',
                                                marginRight: '5px',
                                            }}
                                            title="Conference is being recorded"
                                        />
                                    )}
                                    Room: <strong>{channelInfo.title || ''}</strong>
                                </h2>
                            </div>
                            <div>
                                <MeetingTimer channelInfo={channelInfo} />
                            </div>
                        </div>

                        <div
                            css={css`
                                @media (max-width: 900px) {
                                    display: none;
                                }
                            `}
                        ></div>
                    </div>

                    <GridLayout
                        allParticipantsInfo={allParticipantsInfo}
                        participants={participants}
                        room={room}
                        user={user}
                        dominantSpeaker={dominantSpeaker}
                        audio={audio}
                        video={video}
                        sharingScreen={sharingScreen}
                        participantInfo={participantInfo}
                        localScreenRef={localScreenRef}
                        localVideoRef={localVideoRef}
                    />
                </div>
                {sideMenuActiveTab && (
                    <SideMenu
                        conferenceId={conferenceId}
                        conferenceHostId={conferenceHostId}
                        activeTab={sideMenuActiveTab}
                        handleCloseSideMenu={handleCloseSideMenu}
                        allParticipantsInfo={allParticipantsInfo}
                        waitingParticipants={waitingParticipants}
                        participants={participantInfo}
                        twilioParticipants={participants}
                        channelInfo={channelInfo}
                        handleMuteParticipant={handleMuteParticipant}
                        isHost={isHost}
                        handleRemoveParticipant={handleRemoveParticipant}
                        conferenceSettings={conferenceSettings}
                        updateConferenceSettings={updateConferenceSettings}
                    />
                )}
            </div>

            <div className="controllersDiv">
                <div></div>
                <div className="controls mainControls">
                    <div className="Btn" onClick={handleScreenShare}>
                        {sharingScreen ? (
                            <MdScreenShare fontSize="20" color="#28a745" />
                        ) : (
                            <MdStopScreenShare fontSize="20" />
                        )}
                    </div>
                    <div className="Btn" onClick={handleCamera}>
                        {video ? <FiCamera fontSize="20" /> : <FiCameraOff fontSize="20" />}
                    </div>
                    <div className="Btn" onClick={handleMute}>
                        {audio ? <AiOutlineAudio fontSize="20" /> : <AiOutlineAudioMuted fontSize="20" />}
                    </div>
                    {isHost && (
                        <div className="Btn" onClick={handleConferenceRecording}>
                            {recording ? (
                                <FaStop fontSize="20" color="#D73535" title="Stop Recording" />
                            ) : (
                                <BsRecordCircle fontSize="20" title="Record" />
                            )}
                        </div>
                    )}
                    {isHost ? (
                        <div
                            className="Btn"
                            css={css`
                                .dropup .dropdown-toggle::after {
                                    content: none;
                                }
                                button {
                                    background: transparent;
                                    border: 0;
                                    box-shadow: none;
                                    :focus {
                                        box-shadow: none;
                                    }
                                }
                                .dropdown-item {
                                    font-size: 14px;
                                }
                            `}
                        >
                            <DropdownButton
                                as={ButtonGroup}
                                id={`dropdown-button-drop-up`}
                                drop={'up'}
                                variant="link"
                                style={{ color: '#D73535' }}
                                title={<MdCallEnd fontSize="26" color="#D73535" />}
                            >
                                <Dropdown.Item eventKey="1" onClick={handleDisconnect}>
                                    Leave
                                </Dropdown.Item>
                                <Dropdown.Item eventKey="2" onClick={handleEndCall}>
                                    End Call
                                </Dropdown.Item>
                            </DropdownButton>
                        </div>
                    ) : (
                        <div className="Btn" onClick={handleDisconnect}>
                            <MdCallEnd fontSize="26" color="#D73535" />
                        </div>
                    )}
                </div>
                <div className="controls sideControls">
                    {isMobileView ? (
                        <div
                            className="Btn"
                            css={css`
                                .dropup .dropdown-toggle::after {
                                    content: none;
                                }
                                button {
                                    background: transparent;
                                    border: 0;
                                    box-shadow: none;
                                    color: inherit;
                                    :focus {
                                        box-shadow: none;
                                    }
                                }
                                .dropdown-item {
                                    font-size: 14px;
                                }
                            `}
                        >
                            <DropdownButton
                                as={ButtonGroup}
                                id={`dropdown-mobile-controls`}
                                drop={'up'}
                                alignRight
                                variant="link"
                                title={<BsThreeDotsVertical fontSize="18" />}
                            >
                                <Dropdown.Item eventKey="3" onClick={handleShareConferenceLink}>
                                    Share Conference Link
                                </Dropdown.Item>
                                <Dropdown.Item eventKey="1" onClick={() => handleSideMenuActiveTab('People')}>
                                    People
                                    {isHost && waitingParticipants && waitingParticipants.length > 0
                                        ? ` (${waitingParticipants.length})`
                                        : ''}
                                </Dropdown.Item>
                                <Dropdown.Item eventKey="4" onClick={() => handleSideMenuActiveTab('Chat')}>
                                    Channel chat
                                </Dropdown.Item>
                                {isHost && (
                                    <Dropdown.Item eventKey="2" onClick={() => handleSideMenuActiveTab('Settings')}>
                                        Settings
                                    </Dropdown.Item>
                                )}
                            </DropdownButton>
                        </div>
                    ) : (
                        <>
                            <div className="Btn" onClick={handleShareConferenceLink} title="Share Conference Link">
                                <FiShare2 />
                            </div>
                            <div className="Btn" onClick={() => handleSideMenuActiveTab('People')} title='Participants'>
                                <BsPeopleFill />
                                {isHost && waitingParticipants && waitingParticipants.length > 0 && (
                                    <span className="badge badge-warning">{waitingParticipants.length}</span>
                                )}
                            </div>
                            <div className="Btn" onClick={() => handleSideMenuActiveTab('Chat')} title="Channel chat">
                                <BsChatDotsFill />
                            </div>
                            {isHost && (
                                <div className="Btn" onClick={() => handleSideMenuActiveTab('Settings')} title='Settings'>
                                    <FiSettings />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </RoomContainer>
    );
};

const mapStateToProps = (state) => ({
    user: state.user.user,
});

export default connect(mapStateToProps, { getChannelMembers, getTeamMember })(Room);
